import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DatabaseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  IconButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import {
  Popover,
  Space,
  Spin,
  Switch,
  TableProps,
  Tooltip,
  Typography,
} from 'antd';
import { ReactComponent as CheckCircleIcon } from 'assets/images/icons/checkCircleIcon.svg';
import { ReactComponent as DownloadIcon } from 'assets/images/icons/downloadIcon.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as ExclamationIcon } from 'assets/images/icons/exclamationIcon.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { ReactComponent as HistoryIcon } from 'assets/images/icons/historyIcon.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as RefreshIcon } from 'assets/images/icons/refreshIcon.svg';
import { ReactComponent as StarIcon } from 'assets/images/icons/starIcon.svg';
import { ReactComponent as UserIcon } from 'assets/images/icons/userIcon.svg';
import { ReactComponent as WarningIcon } from 'assets/images/icons/warningIcon.svg';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import {
  CardDescSpan,
  DashboardContainer,
  GrayItalicSpan,
} from 'components/Custom/spans';
import { LocalizedNumber } from 'components/LocalizedNumber';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { applyFilters as applyFiltersPipeline } from 'filter-system';
import moment from 'moment';
import { FILTER_BOX_SELECTED } from 'pages/vies/customers-suppliers';
import React, { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ContactType, VIESContactStatistic, ViesStatus } from 'store/app/enums';
import { Organisation } from 'store/organisation/models';
import { Contact, HideContactData, ViesInfo } from 'store/vat-checker/models';
import { getContactTypeLabel } from 'utils/display-enum-label';
import { getVIESProviderLink } from 'utils/get-provider-link';
import { Preference, PreferenceActions } from 'utils/preferences';
import { StatusGetter } from 'utils/status-getter';

interface CustomersAndSuppliersProps {
  loading: boolean;
  contacts?: Contact[];
  organisation?: Organisation;
  openDownloadCertificateModal: (record: Contact, invoice?: string) => void;
  openConfirmWindow: () => void;
  checkContact: (contactId: string) => void;
  getHistoryLogs: (record: Contact) => void;
  viesInfo?: ViesInfo;
  onRefreshContacts: (connectionId: string) => void;
  onRefreshContact: (contactId: string, connectionId: string) => void;
  handlePreferences: (
    action: PreferenceActions,
    preference: Preference
  ) => void;
  hideContact: (hideContactData: HideContactData) => void;
}

const { Text } = Typography;
export const PAGE_SIZE_VIES = 'page-size-vies';

export const CustomersAndSuppliers: React.FC<CustomersAndSuppliersProps> = ({
  loading,
  contacts,
  organisation,
  openDownloadCertificateModal,
  openConfirmWindow,
  checkContact,
  getHistoryLogs,
  viesInfo,
  onRefreshContacts,
  onRefreshContact,
  handlePreferences,
  hideContact,
}) => {
  const pageSizeSaved = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_VIES,
  });
  const { t } = useTranslation();
  const filterBoxSelected: any = handlePreferences(PreferenceActions.GET, {
    name: FILTER_BOX_SELECTED,
  });
  const [selectedContaniers, setSelectedContaniers] = useState<any>([]);
  const [showHiddenContacts, setShowHiddenContacts] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [filtersState, setFiltersState] = useState<Record<string, any>>({});
  const [baseFilteredData, setBaseFilteredData] = useState<
    Contact[] | undefined
  >(contacts);
  const [statusFilter, setStatusFilter] = useState('');

  // Pure function to apply a single filter
  const applyFilter = (
    data: Contact[] | undefined,
    filterKey: string
  ): Contact[] | undefined => {
    if (!data) return data;

    if (filterKey === VIESContactStatistic.Customers) {
      return data.filter(
        (item) =>
          item.contactType &&
          (ContactType[item.contactType] == ContactType.CUSTOMER ||
            ContactType[item.contactType] == ContactType.BOTH)
      );
    }

    if (filterKey === VIESContactStatistic.Suppliers) {
      return data.filter(
        (item) =>
          item.contactType &&
          (ContactType[item.contactType] == ContactType.SUPPLIER ||
            ContactType[item.contactType] == ContactType.BOTH)
      );
    }

    if (filterKey === VIESContactStatistic.ContactsErrors) {
      return data.filter(
        (item) =>
          item.viesStatus == ViesStatus.ERROR ||
          item.viesStatus == ViesStatus.INVALID ||
          item.viesStatus == ViesStatus.SYSTEM_UNAVAILABLE
      );
    }

    if (filterKey === VIESContactStatistic.StatusMissing) {
      return data.filter((item) => item.viesStatus == ViesStatus.MISSING);
    }

    return data;
  };

  // Recompute filtered data whenever contacts or selectedContaniers changes
  useEffect(() => {
    if (!contacts) {
      setBaseFilteredData(undefined);
      return;
    }

    let filtered: Contact[] = [...contacts];

    // Apply all active filters in sequence
    for (const filterKey of selectedContaniers) {
      const result = applyFilter(filtered, filterKey);
      if (result) {
        filtered = result;
      }
    }

    setBaseFilteredData(filtered);
  }, [contacts, selectedContaniers]);

  const pipelineFilteredContacts = useMemo(() => {
    if (!baseFilteredData) return undefined;
    if (!filtersState || Object.keys(filtersState).length === 0) {
      return baseFilteredData;
    }
    return (
      applyFiltersPipeline(baseFilteredData, filtersState, {
        mainLocaleKey: 'vat-checker',
      }) ?? baseFilteredData
    );
  }, [baseFilteredData, filtersState]);

  const dataSource = useMemo(
    () =>
      pipelineFilteredContacts?.filter(
        (contact) => contact.isVisible == !showHiddenContacts
      ),
    [pipelineFilteredContacts, showHiddenContacts]
  );

  // Initialize filters from saved preferences
  useEffect(() => {
    if (filterBoxSelected && !selectedContaniers.includes(filterBoxSelected)) {
      setSelectedContaniers([filterBoxSelected]);
    }
  }, [filterBoxSelected]);

  const toggleFilter = (filterKey: string) => {
    setSelectedContaniers((prev) => {
      let newFilters: string[];

      // Handle mutual exclusivity for Customers and Suppliers
      if (
        filterKey === VIESContactStatistic.Customers ||
        filterKey === VIESContactStatistic.Suppliers
      ) {
        if (prev.includes(filterKey)) {
          // If the filter is already selected, remove it
          newFilters = prev.filter((key) => key !== filterKey);
        } else {
          // If selecting a new filter, remove the other mutually exclusive filter first
          newFilters = prev.filter(
            (key) =>
              key !== VIESContactStatistic.Customers &&
              key !== VIESContactStatistic.Suppliers
          );
          newFilters.push(filterKey);
        }
      } else {
        // For non-mutually exclusive filters, use normal toggle behavior
        newFilters = prev.includes(filterKey)
          ? prev.filter((key) => key !== filterKey)
          : [...prev, filterKey];
      }

      // Update preferences
      const keyToSave = newFilters.length > 0 ? newFilters[0] : '';
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_BOX_SELECTED,
        value: keyToSave,
      });

      return newFilters;
    });
  };

  const removeFilter = (filterKey: string) => {
    setSelectedContaniers((prev) => {
      const newFilters = prev.filter((key) => key !== filterKey);

      // Update preferences
      const keyToSave = newFilters.length > 0 ? newFilters[0] : '';
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_BOX_SELECTED,
        value: keyToSave,
      });

      return newFilters;
    });
  };

  // Legacy function for backward compatibility - now just calls toggleFilter
  const filterContacts = (key: string, useOriginal = false) => {
    toggleFilter(key);
  };

  // Legacy function for backward compatibility - simplified
  const filterContactsPerType = (addedKey: string, removedKey: string) => {
    setSelectedContaniers((prev) => {
      const newFilters = prev.filter((key) => key !== removedKey);
      if (!newFilters.includes(addedKey)) {
        newFilters.push(addedKey);
      }
      return newFilters;
    });
  };

  // Legacy function for backward compatibility - now just calls removeFilter
  const removeSelection = (key: string) => {
    removeFilter(key);

    if (
      key == VIESContactStatistic.StatusMissing ||
      key == VIESContactStatistic.ContactsErrors
    ) {
      setStatusFilter('');
    }
  };

  const onHideContactClick = (isActive: boolean, contactUuid: string) => {
    const hideContactData: HideContactData = {
      isActive,
      contactsToHide: [contactUuid],
    };
    hideContact(hideContactData);

    setTimeout(() => {
      setSelectedRowKeys([]);
    }, 1000);
  };

  const onHideContactsClick = (isActive: boolean) => {
    const hideContactData: HideContactData = {
      isActive, //@ts-ignore
      contactsToHide: selectedRowKeys,
    };
    hideContact(hideContactData);

    setTimeout(() => {
      setSelectedRowKeys([]);
    }, 1000);
  };

  const calcContactsType = (type) => {
    return dataSource?.filter(
      (item) =>
        item.contactType &&
        (ContactType[item.contactType] == ContactType[type] ||
          ContactType[item.contactType] == ContactType.BOTH)
    )?.length;
  };

  const calcContactsStatus = (status) => {
    return dataSource?.filter((item) => item.viesStatus == status)?.length;
  };

  const calcErrorsStatus = () => {
    return dataSource?.filter(
      // ERROR MODI
      (item) =>
        item.viesStatus == ViesStatus.ERROR ||
        item.viesStatus == ViesStatus.INVALID ||
        item.viesStatus == ViesStatus.SYSTEM_UNAVAILABLE
    )?.length;
  };

  const rowSelection: TableProps<Contact>['rowSelection'] = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: Contact[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: Contact) => ({
      name: record.name,
    }),
  };

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    } else {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
  };

  const getHeaderForExport = (dataToExport) => {
    const obj = dataToExport?.length > 0 ? dataToExport[0] : null;
    let headerArr: any = [];
    if (obj) {
      const headers = Object.keys(obj);
      headerArr = headers.map((key) => ({
        label: key.replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
        key: key,
      }));
    }

    return headerArr;
  };

  const prepareDataToExport = (dataToExport: Contact[]) => {
    if (Object.keys(ViesStatus).includes(dataToExport[0].viesStatus)) {
      const date = dataToExport.map((contact) => {
        contact.viesStatus = ViesStatus[contact.viesStatus]; //@ts-ignore
        contact.lastValidCheck = contact.lastValidCheck?.viesCheckedDate;
        contact.name = contact.name ? contact.name.replaceAll('"', '') : '';
        contact.country = contact.country
          ? contact.country.replaceAll('"', '')
          : '';
        contact.address = contact.address
          ? contact.address.replaceAll('"', '')
          : '';
        contact.address1 = contact.address1
          ? contact.address1.replaceAll('"', '')
          : '';

        contact.phone = contact.phone ? contact.phone.replaceAll('"', '') : '';
        contact.email = contact.email ? contact.email.replaceAll('"', '') : '';
        contact.taxNumber = contact.taxNumber
          ? contact.taxNumber.replaceAll('"', '')
          : '';
        return contact;
      });
      return date;
    } else {
      return dataToExport;
    }
  };

  const renderCustomizeSection = () => {
    return (
      <Space>
        <GrayItalicSpan color='var(--color-text-muted, #5a6a6a)'>
          {viesInfo?.lastViesCheckDate &&
            `${t('vat-checker.last-check')}: ${moment(viesInfo?.lastViesCheckDate).fromNow()}`}
        </GrayItalicSpan>
        <IconButton //@ts-ignore
          width="107px"
          height="30px"
          fontSize="12px"
          lineheight="12px"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}
          onClick={() => openConfirmWindow()}
          icon={<CheckCircleIcon width={'16px'} height={'16px'} />}
          type="default"
          loading={loading}
          disabled={selectedRowKeys.length > 0}
        >
          {t('vat-checker.check-all-vat')}
        </IconButton>
        <IconButton
          loading={loading} //@ts-ignore
          width="225px"
          height="30px"
          fontSize="12px"
          lineheight="12px"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}
          onClick={() => {
            viesInfo && onRefreshContacts(viesInfo?.connectionId);
          }}
          icon={
            <RefreshIcon
              width={'18px'}
              height={'18px'}
              style={{
                filter:
                  'brightness(0) saturate(100%) invert(62%) sepia(19%) saturate(146%) hue-rotate(131deg) brightness(90%) contrast(88%)',
              }}
            />
          }
          type="default"
          disabled={selectedRowKeys.length > 0}
        >
          {t('vat-checker.refresh-customers-suppliers')}
        </IconButton>
        <IconButton
          loading={loading} //@ts-ignore
          width="225px"
          height="30px"
          fontSize="12px"
          lineheight="12px"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}
          icon={<IconExport style={{ width: 20, height: 20 }} />}
        >
          <CSVLink
            className="link-without-underline"
            filename={`${organisation?.friendlyName} - contacts.csv`}
            data={
              contacts && contacts.length > 0
                ? prepareDataToExport(contacts)
                : []
            }
            headers={getHeaderForExport(
              contacts && contacts?.length > 0 ? contacts : []
            )}
          >
            <span>{t('vat-checker.export-list')}</span>
          </CSVLink>
        </IconButton>
        <IconButton //@ts-ignore
          width="107px"
          height="30px"
          fontSize="12px"
          lineheight="12px"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}
          disabled={selectedRowKeys.length == 0}
          onClick={() => onHideContactsClick(showHiddenContacts)}
          icon={
            showHiddenContacts ? (
              <EyeOutlined style={{ fontSize: '16px', color: 'var(--color-text-muted, #5a6a6a)' }} />
            ) : (
              <EyeInvisibleOutlined
                style={{ fontSize: '16px', color: 'var(--color-text-muted, #5a6a6a)' }}
              />
            )
          }
          type="default"
          loading={loading}
        >
          {showHiddenContacts
            ? t('vat-checker.unhide-selected')
            : t('vat-checker.hide-selected')}
        </IconButton>
      </Space>
    );
  };

  const renderAppendedFilter = () => {
    return (
      <Space>
        <GrayItalicSpan color='var(--color-text-muted, #5a6a6a)'>
          {t('vat-checker.show-hidden')}
        </GrayItalicSpan>
        <Tooltip
          title={
            showHiddenContacts
              ? t('common.toggle-on-click-to-turn-off')
              : t('common.toggle-off-click-to-turn-on')
          }
        >
          <Switch
            className={showHiddenContacts ? 'switch-checked' : ''}
            checked={showHiddenContacts}
            aria-label={t('vat-checker.show-hidden')}
            onChange={(
              checked: boolean,
              _event: React.MouseEvent<HTMLButtonElement>
            ) => {
              setShowHiddenContacts(checked);
            }}
          />
        </Tooltip>
      </Space>
    );
  };

  const clearAppendedFilter = () => {
    setShowHiddenContacts(false);
  };

  const renderContact = (content: string, record: Contact) => {
    const to = `/vat-checker/contact-details/connection/${organisation?.connectionId}/contact/${record.contactUuid}`;
    const viesHref = getVIESProviderLink(
      record.providerName?.toLowerCase(),
      organisation?.shortCode,
      record?.contactType && ContactType[record?.contactType].toLowerCase(),
      record.providerContactId
    );
    const logoImg = (
      <ProviderIcon
        providerName={record.providerName}
        alt={record?.providerName}
        width={25}
        style={{ paddingTop: '4px' }}
      />
    );
    const logoWrap =
      viesHref != null && viesHref !== '' ? (
        <a
          href={viesHref}
          target="_blank"
          rel="noreferrer"
          style={{ height: '32px', display: 'block' }}
        >
          {logoImg}
        </a>
      ) : (
        <ProviderLogoNav
          providerName={record.providerName}
          shortCode={organisation?.shortCode}
          organisationId={organisation?.uuid}
          style={{ height: '32px', display: 'block' }}
        >
          {logoImg}
        </ProviderLogoNav>
      );
    return (
      <Space size={'small'} style={{ minWidth: '140px' }}>
        {logoWrap}
        <Link to={to}>
          <Text
            className="underline-link"
            style={{ minWidth: 150, maxWidth: 150 }} //@ts-ignore
            ellipsis={{ rows: 1, tooltip: true }}
          >
            {content}
          </Text>
        </Link>
      </Space>
    );
  };

  const renderContextMenu = (record: Contact) => {
    const to = `/vat-checker/contact-details/connection/${organisation?.connectionId}/contact/${record.contactUuid}`;
    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <IconContextButton
            type="text"
            icon={
              <CheckCircleIcon
                width={'16px'}
                height={'16px'}
              />
            }
            onClick={() => {
              checkContact(record.contactUuid);
            }}
          >
            {t('vat-checker.check-vat')}
          </IconContextButton>
          <IconContextButton
            loading={loading}
            type="text"
            icon={<RefreshIcon width={'18px'} height={'18px'} />}
            onClick={() => {
              viesInfo &&
                onRefreshContact(record.contactUuid, viesInfo?.connectionId);
            }}
          >
            {t('vat-checker.refresh-data')}
          </IconContextButton>
          <IconContextButton
            type="text"
            icon={<HistoryIcon width={'18px'} height={'18px'} />}
            onClick={() => {
              getHistoryLogs(record);
            }}
          >
            {t('vat-checker.history-log')}
          </IconContextButton>
          {record.lastValidCheck && (
            <IconContextButton
              type="text"
              icon={<DownloadIcon width={'18px'} height={'18px'} />}
              onClick={() => {
                const lastViesCheck =
                  typeof record.lastValidCheck == 'string'
                    ? record.lastValidCheck
                    : record.lastValidCheck?.viesCheckedDate;
                const contactToSend = { ...record, lastViesCheck } as Contact;

                openDownloadCertificateModal(
                  contactToSend,
                  record.lastValidCheck?.invoiceNumber
                    ? record.lastValidCheck?.invoiceNumber
                    : ''
                );
              }}
            >
              {t('vat-checker.download-last-certificate')}
            </IconContextButton>
          )}
          <IconContextButton
            type="text"
            icon={
              <DatabaseOutlined
                style={{ fontSize: '16px', width: '18px', height: '20px' }}
              />
            }
          >
            <Link style={{ textDecoration: 'none' }} to={to}>
              {t('vat-checker.view-contact')}
            </Link>
          </IconContextButton>
          {record.isVisible ? (
            <IconContextButton
              type="text"
              icon={
                <EyeInvisibleOutlined
                  style={{ fontSize: '16px', width: '18px', height: '20px' }}
                />
              }
              onClick={() => {
                onHideContactClick(!record.isVisible, record.contactUuid);
              }}
            >
              {t('vat-checker.hide-contact')}
            </IconContextButton>
          ) : (
            <IconContextButton
              type="text"
              icon={<EyeIcon width={'18px'} height={'22px'} />}
              onClick={() => {
                onHideContactClick(!record.isVisible, record.contactUuid);
              }}
            >
              {t('vat-checker.show-contact')}
            </IconContextButton>
          )}
        </Space>
      </div>
    );
  };

  const columns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      //ellipsis: true,
      render: renderContact /* (content: string) => (
        <Text
          style={{ minWidth: 400, maxWidth: 500 }}
          //ellipsis={{ rows: 1, tooltip: true }}
        >
          {record.providerName}-{content}
        </Text>
      ) */,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortIcon: renderSortIcon,
    },
    {
      title: t('vat-checker.vat-no'),
      dataIndex: 'taxNumber',
      key: 'taxNumber',
      render: (_, record: Contact) => (
        <>
          {record.taxNumber && record.taxNumber != 'null'
            ? record.taxNumber
            : '-'}
        </>
      ),
      sorter: (a, b) => a.taxNumber?.localeCompare(b.taxNumber),
      sortIcon: renderSortIcon,
    },
    {
      title: t('common.country'),
      dataIndex: 'country',
      key: 'country',
      sorter: (a, b) => a.country?.localeCompare(b.country),
      sortIcon: renderSortIcon,
    },
    {
      title: t('vat-checker.type'),
      key: 'contactType',
      render: (_, record: Contact) => (
        <>{record.contactType ? getContactTypeLabel(record.contactType, t) : '-'}</>
      ),
    },
    {
      title: t('vat-checker.last-certificate'),
      render: (_content: string, record: Contact) => {
        if (!record.lastValidCheck) return <span>-</span>;
        else
          return (
            <Space>
              <span>
                {record &&
                  record.lastValidCheck &&
                  moment(
                    typeof record.lastValidCheck == 'string'
                      ? record.lastValidCheck
                      : record.lastValidCheck.viesCheckedDate
                  ).format('DD/MM/YYYY')}
              </span>
              <IconButton
                onClick={() => {
                  const lastViesCheck =
                    typeof record.lastValidCheck == 'string'
                      ? record.lastValidCheck
                      : record.lastValidCheck?.viesCheckedDate;
                  const contactToSend = { ...record, lastViesCheck } as Contact;

                  openDownloadCertificateModal(
                    contactToSend,
                    record.lastValidCheck?.invoiceNumber
                      ? record.lastValidCheck?.invoiceNumber
                      : ''
                  );
                }}
                icon={
                  <DownloadIcon
                    width={'18px'}
                    height={'18px'}
                    style={{
                      filter:
                        'brightness(0) saturate(100%) invert(62%) sepia(19%) saturate(146%) hue-rotate(131deg) brightness(90%) contrast(88%)',
                    }}
                  />
                }
                type="default"
              ></IconButton>
            </Space>
          );
      },
    },
    {
      title: t('vat-checker.last-check'),
      dataIndex: 'lastViesCheck',
      key: 'lastViesCheck',
      render: (_content: string, record: Contact) => {
        return (
          <Space>
            <div style={{ width: 130 }}>
              {record &&
                moment(record.lastViesCheck).format('DD/MM/YYYY hh:mm:ss')}
            </div>
            <IconButton
              disabled={record.viesStatus == 'EMPTY'}
              onClick={() => checkContact(record.contactUuid)}
              icon={
                <CheckCircleIcon
                  width={'16px'}
                  height={'16px'}
                  style={{
                    margin: '0 6px',
                    filter:
                      'brightness(0) saturate(100%) invert(62%) sepia(19%) saturate(146%) hue-rotate(131deg) brightness(90%) contrast(88%)',
                  }}
                />
              }
              type="default"
            ></IconButton>
          </Space>
        );
      },
      sorter: (a, b) =>
        new Date(a.lastViesCheck).valueOf() -
        new Date(b.lastViesCheck).valueOf(), //a.lastViesChack?.localeCompare(b.lastViesChack),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.status'),
      dataIndex: 'viesStatus',
      key: 'viesStatus',
      render: (content) => {
        return content ? (
          <StatusGetter
            status={
              ViesStatus[content.toUpperCase().trim().replaceAll(' ', '_')]
            }
            noDraft
          />
        ) : null;
      },
      sorter: (a, b) => a.viesStatus.localeCompare(b.viesStatus),
      sortIcon: renderSortIcon,
    },

    {
      title: t('submissions.actions-header'),
      render: (_, record: Contact) => (
        <Space>
          <ScopeButton //@ts-ignore
            width="98px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            type="primary"
            onClick={() => getHistoryLogs(record)}
          >
            {t('vat-checker.history-log')}
          </ScopeButton>

          <Popover
            placement="topLeft"
            arrow={false}
            trigger="click"
            content={renderContextMenu(record)}
            title={''}
            overlayStyle={{ zIndex: 999 }}
          >
            <ContextButton type="text" aria-label={t('common.more-actions')}>
              <EllipseIcon
                style={{
                  width: 18,
                  height: 18,
                }}
              />
            </ContextButton>
          </Popover>
        </Space>
      ),
    },
  ];
  const savePageSize = (pageSize: number) => {
    handlePreferences(PreferenceActions.SET, {
      name: PAGE_SIZE_VIES,
      value: pageSize,
    });
  };
  const renderHeaderComponent = () => {
    return (
      <Space direction="horizontal" style={{ float: 'right' }}>
        {selectedRowKeys.length > 0 && (
          <CardDescSpan
            style={{
              float: 'right',
              width: 400,
              display: 'flex',
              justifyContent: 'end',
            }}
          >
            {t('vat-checker.items-selected', { count: selectedRowKeys.length })}
          </CardDescSpan>
        )}
      </Space>
    );
  };

  const [hasScrollbar, setHasScrollbar] = React.useState(false);

  React.useEffect(() => {
    const checkScrollbar = () => {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        const hasVerticalScrollbar =
          document.documentElement.scrollHeight >
          document.documentElement.clientHeight;
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        setHasScrollbar(hasVerticalScrollbar && scrollbarWidth > 0);
      }, 100);
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    window.addEventListener('scroll', checkScrollbar);
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (document.body) {
      resizeObserver.observe(document.body);
    }

    // Also check after a delay to catch late-rendered content
    const timeoutId = setTimeout(checkScrollbar, 500);

    return () => {
      window.removeEventListener('resize', checkScrollbar);
      window.removeEventListener('scroll', checkScrollbar);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [contacts, loading, dataSource]);

  return (
    <div
      style={{
        padding: '40px',
        paddingTop: '10px',
        maxWidth: hasScrollbar ? 1662 : 1678,
        minWidth: 1200,
        margin: 'auto',
      }}
    >
      <Spin
        spinning={loading || !organisation}
        size="large"
        style={{ width: '100%', height: '100%' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '25px',
          }}
        >
          <DashboardContainer
            className={
              selectedContaniers.includes(VIESContactStatistic.Customers)
                ? 'selected-container'
                : 'non-selected-container'
            }
            onClick={() => {
              toggleFilter(VIESContactStatistic.Customers);
            }}
          >
            <Space direction="vertical" style={{ width: '100%', rowGap: 6 }}>
              <UserIcon className="container-icon" />
              <Text className="container-title">
                {t('vat-checker.customers')}
              </Text>
              <LocalizedNumber
                className="container-number"
                value={calcContactsType('CUSTOMER')}
              />
            </Space>
          </DashboardContainer>
          <DashboardContainer
            className={
              selectedContaniers.includes(VIESContactStatistic.Suppliers)
                ? 'selected-container'
                : 'non-selected-container'
            }
            onClick={() => {
              toggleFilter(VIESContactStatistic.Suppliers);
            }}
          >
            <Space direction="vertical" style={{ width: '100%', rowGap: 6 }}>
              <StarIcon className="container-icon" />
              <Text className="container-title">
                {t('vat-checker.suppliers')}
              </Text>
              <LocalizedNumber
                className="container-number"
                value={calcContactsType('SUPPLIER')}
              />
            </Space>
          </DashboardContainer>
          <DashboardContainer
            className={
              selectedContaniers.includes(VIESContactStatistic.ContactsErrors)
                ? 'selected-container'
                : 'non-selected-container'
            }
            onClick={() => {
              toggleFilter(VIESContactStatistic.ContactsErrors);
            }}
          >
            <Space direction="vertical" style={{ width: '100%', rowGap: 6 }}>
              <WarningIcon className="container-icon" />
              <Text className="container-title">
                {t('vat-checker.contacts-errors')}
              </Text>
              <LocalizedNumber
                className="container-number"
                value={calcErrorsStatus()}
              />
            </Space>
          </DashboardContainer>
          <DashboardContainer
            className={
              selectedContaniers.includes(VIESContactStatistic.StatusMissing)
                ? 'selected-container'
                : 'non-selected-container'
            }
            onClick={() => {
              toggleFilter(VIESContactStatistic.StatusMissing);
            }}
          >
            <Space direction="vertical" style={{ width: '100%', rowGap: 6 }}>
              <ExclamationIcon className="container-icon" />
              <Text className="container-title">
                {t('vat-checker.status-missing')}
              </Text>
              <LocalizedNumber
                className="container-number"
                value={calcContactsStatus('Missing')}
              />
            </Space>
          </DashboardContainer>
        </div>
        {
          //@ts-ignore
          <>
            <ScopeTableFilters
              filters={['name', 'viesStatus', 'country']}
              mainLocaleKey="vat-checker"
              value={filtersState}
              onChange={setFiltersState}
              originalDataSource={contacts}
              tableDataSource={dataSource}
              setSelectedContanier={setSelectedContaniers}
              statusFilterKey={statusFilter}
              appendedFilterComponent={renderAppendedFilter()}
              clearAppendedFilterComponent={clearAppendedFilter}
              embedded={false}
            />
            <ScopeTable
              className={selectedRowKeys.length > 0 ? 'show-selected-items' : ''}
              defaultSort={['name', 'asc']}
              rowSelection={{ type: 'checkbox', ...rowSelection }}
              originalDataSource={contacts}
              tableDataSource={dataSource}
              columns={columns}
              pagination={true} //@ts-ignore
              rowsPerPage={pageSizeSaved ? pageSizeSaved : 20}
              renderRightComponent={renderCustomizeSection()}
              enableColumnsCustomization={false}
              haveBorder
              showSizeChanger
              getNewPageSize={savePageSize}
              titleComponent={renderHeaderComponent()}
            />
          </>
        }
      </Spin>
    </div>
  );
};

